<!--
    Diese Komponente bietet eine svg-Zeichenfläche, auf der beliebige svg-Elemente angezeigt werden können.
    Dazu werden die Elemente in einem Slot übergeben.

    Die Komponente bietet folgende Funktionen:
    - Verschieben der Zeichenfläche
    - Zoomen der Zeichenfläche

    Die Komponente ist so ausgelegt, dass sie auch in anderen Programmen (z.B. DSimWeb) funktionieren sollte.
 -->
<script lang="ts">
    import { decreaseScale, increaseScale } from "./pane_scale";
    import { initialPointerCache, pointerCache_addEntry, pointerCache_removeEntry, pointerCache_updatePosition } from "./pointerCache";
    import IconZoomToFit from "./IconZoomToFit.svelte";
    import type { Vector } from "../Vector";
    import { initialPinchZoom, pinchZoom_execute, pinchZoom_start } from "./pinchZoom";

    export let onPrimaryPointerMove: (pos: Vector) => void;

    export let onClick: (event: MouseEvent, position: Vector) => void;
    export let onScaleChange: (scale: number) => void;
    export let onTranslationChange: (translation: Vector) => void;
    export let onShowParametersChange: (scale: number, translation: Vector) => void;

    export let translation: Vector = {
        x: 0,
        y: 0,
    }

    export let scale = 100;

    export const positionInPane = (x: number, y: number) => {
        let pt = svg.createSVGPoint();
        pt.x = x;
        pt.y = y;

        const svgP = pt.matrixTransform( pane.getScreenCTM().inverse() );

        return {
            x: svgP.x,
            y: svgP.y
        };
    }

    export const zoomToFit = () => {
        handleZoomToFitClick();
    }

    export function getSVG(): string {
        const bBoxSVG = pane.getBBox();
        const viewBox = 'viewBox="' + bBoxSVG.x + ' ' + bBoxSVG.y + ' ' + bBoxSVG.width + ' ' + bBoxSVG.height + '"';

        return '<svg xmlns="http://www.w3.org/2000/svg" ' + viewBox + '>' + pane.innerHTML + '</svg>';
    }

    let dragPane = {
        active: false,

        // Position der Maus beim Start des Drag-Vorgangs
        // im Client-Koordinatensystem (event.clientX, event.clientY)
        dragStartClientPos: {
            x: 0,
            y: 0,
        },

        // translation der Pane beim Start des Drag-Vorgangs
        // im Client-Koordinatensystem
        dragStartTranslation: {
            x: 0,
            y: 0,
        },
    }

    let pinchZoom = initialPinchZoom;

    // der pointerCache speichert alle Pointer, die gerade auf dem SVG-Element aktiv sind
    // wenn ein Pointer bewegt wird, wird die Position im pointerCache aktualisiert
    // im pointerCache wir die Längeneinheit des Koordinatensystems s (SVG-Element) verwendet
    // in einem PointerEvent / Mouseevent: ( offsetX / offsetY )
    let pointerCache = initialPointerCache;

    function handlePointerDown(event: PointerEvent) {
        // console.log('base pane - pointer down');
        // console.log('pane - pointer down; buttons: ', event.buttons, ' button: ', event.button);

        svg.setPointerCapture(event.pointerId);

        if (pointerCache.length === 0) {
            // if (event.button === 0) {
            //     // linke Maustaste
            //     if (ui_editMode === EditMode.NEUTRAL) {
            //         let mousePosition = positionInPane(event.clientX, event.clientY);
            //         state.set(multiselect_startRect($state, mousePosition.x, mousePosition.y));
            //     }
            // } else
            if (event.button === 1 || event.button === 2) {
                // mittlere Maustaste
                dragPane = {
                    active: true,
                    dragStartClientPos: {
                        x: event.clientX,
                        y: event.clientY,
                    },
                    dragStartTranslation: translation,
                }

                // verhindern, dass der Browser den Inhalt der gesamten Webseite verschiebt
                event.preventDefault();
            }
        } else if (pointerCache.length === 1) {
            // state.set(pane_startPinchZoom($state, pointerCache[0].lastPosition, {x: event.offsetX, y: event.offsetY}));
            // state.set(multiselect_abort($state));
            pinchZoom = pinchZoom_start(pointerCache[0].lastPosition, {x: event.offsetX, y: event.offsetY}, scale, translation);
        }

        // Event cachen
        pointerCache = pointerCache_addEntry(pointerCache, event.pointerId, {x: event.offsetX, y: event.offsetY});
    }

    function handlePointerMove(event: PointerEvent) {
        // console.log('base pane - pointer move');
        // aktuelle Position des Pointers im pointerCache speichern
        pointerCache = pointerCache_updatePosition(pointerCache, event.pointerId, {x: event.offsetX, y: event.offsetY});

        const mousePosition = positionInPane(event.clientX, event.clientY);

        if (pointerCache.length === 1) {
            // if (uiState.select.mouseDown) {
            //     let mousePosition = positionInPane(event.clientX, event.clientY);
            //     state.set(multiselect_mouseMove($state, mousePosition.x, mousePosition.y));
            // }

            if (dragPane.active) {
                const deltaXScreen = event.clientX - dragPane.dragStartClientPos.x;
                const deltaYScreen = event.clientY - dragPane.dragStartClientPos.y;

                const newTranslationX = dragPane.dragStartTranslation.x + deltaXScreen;
                const newTranslationY = dragPane.dragStartTranslation.y + deltaYScreen;

                onTranslationChange({x: newTranslationX, y: newTranslationY});
            }
        } else if (pointerCache.length === 2) {
            // Pinch-Zoom
            const {scale: newScale, translate: newTranslate } = pinchZoom_execute(pinchZoom, pointerCache[0].lastPosition, pointerCache[1].lastPosition);
            onShowParametersChange(newScale, newTranslate);

        }

        // mainPointerPosition.set(mousePosition);
        onPrimaryPointerMove(mousePosition);
    }

    function handlePointerUp(event: PointerEvent) {
        // console.log('base pane - pointer up');

        if (event.button === 0) {
            // if (ui_editMode === EditMode.NEUTRAL && uiState.select.mouseDown) {
            //     state.set(multiselect_stop($state));
            // }
        } else if(event.button === 1 || event.button === 2) {
            if (dragPane.active) {
                dragPane = {
                    ...dragPane,
                    active: false,
                }
            }
        }

        // Cacheeintrag für diesen Pointer-Event löschen
        // pointerCache = pointerCache.filter(cacheEntry => cacheEntry.pointerId !== event.pointerId)
        pointerCache = pointerCache_removeEntry(pointerCache, event.pointerId);
        if (pointerCache.length < 2) {
            // state.set(pane_stopPinchZoom($state));
        }
    }

    function handlePointerCancel(event: PointerEvent) {

        // console.log('base pane - pointer cancel ' + event.type);

        // Cacheeintrag für diesen Pointer-Event löschen
        // console.log('pointer cancel ', event.type)
        pointerCache = pointerCache.filter(cacheEntry => cacheEntry.pointerId !== event.pointerId);

        if (pointerCache.length < 2) {
            // state.set(pane_stopPinchZoom($state));
        }

        if (pointerCache.length === 0) {
            // state.set(multiselect_abort($state));
            // state.set(pane_dragStop($state));
        }
    }

    function handleWheel(event: WheelEvent) {
        // console.log('base pane - handleWheel');
        // if (event.ctrlKey) {
            event.preventDefault();

            // vgl. Dokumentation zum Pinch-Zoom
            // Die Position des Mauszeigers wird als Fokus verwendet
            const newScale = event.deltaY > 0 ? increaseScale(scale) : decreaseScale(scale);
            const deltaTranslationScaleX = (1 - newScale/scale) * (event.offsetX - translation.x);
            const deltaTranslationScaleY = (1 - newScale/scale) * (event.offsetY - translation.y);

            onShowParametersChange(newScale, {
                x: translation.x + deltaTranslationScaleX,
                y: translation.y + deltaTranslationScaleY,
            });
        // }
    }

    function handleClick(event: MouseEvent) {
        // console.log('base pane - click');

        onClick(event, positionInPane(event.clientX, event.clientY));
        // dispatch('click', {
        //     pointerPositionInPane: positionInPane(event.clientX, event.clientY),
        //     pointerEvent: event,
        // });
    }

    function max(a: number, b: number) {
        if (a > b) {
            return a;
        } else {
            return b;
        }
    }

    function min(a: number, b: number) {
        if (a < b) {
            return a;
        } else {
            return b;
        }
    }

    function handleZoomToFitClick() {
        const zoomToFitMargin = 20;

        const bBoxPane = pane.getBBox();
        const svgWidth = svg.width.baseVal.value;
        const svgHeight = svg.height.baseVal.value;

        // console.log(svgWidth, svgHeight, bBoxPane.width, bBoxPane.height, bBoxPane.x, bBoxPane.y)

        const zoomX = (svgWidth - zoomToFitMargin*2) / bBoxPane.width;
        const zoomY = (svgHeight - zoomToFitMargin*2) / bBoxPane.height;

        // Der neue Zommfaktor ist der kleinere der beiden Zoomfaktoren, aber höchstens 200
        const newScale = min(min(zoomX, zoomY) * 100, 200);

        const newTranslation: Vector = {
            x: -bBoxPane.x * newScale / 100 + zoomToFitMargin,
            y: -bBoxPane.y * newScale / 100 + zoomToFitMargin,
        }

        onShowParametersChange(newScale, newTranslation);
    }

    let svg: SVGSVGElement;
    let pane: SVGGElement;

    // $: {
    //     console.log('base pane - translation: ', translation, ' scale: ', scale);
    // }
</script>


<div style="width: 100%; height: 100%; background-color: white; border-radius: 10px; touch-action: none;">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <svg
        bind:this={svg}
        on:pointerdown={handlePointerDown}
        on:pointermove={handlePointerMove}
        on:pointerup={handlePointerUp}

        on:pointercancel = {handlePointerCancel}
        on:pointerout = {handlePointerCancel}
        on:pointerleave = {handlePointerCancel}

        on:click={handleClick}
        on:wheel = {handleWheel}
        style="user-select: none; width: 100%; height: 100%"
    >
        <!-- <g transform="translate({translation.x},{translation.y}) scale({scale / 100})">
            <line stroke="black" x1={-5} y1={0} x2={5} y2={0}></line>
            <line stroke="black" x1={0} y1={-5} x2={0} y2={5}></line>
        </g> -->
        <g bind:this={pane} transform="translate({translation.x},{translation.y}) scale({scale / 100})" >
            <!-- <line stroke="black" x1={0} y1={0} x2={5} y2={0}></line> -->
            <slot />
        </g>
    </svg>
    <button
        style="position: absolute; bottom: 20px; right: 20px;"
        on:click={handleZoomToFitClick}
    >
        <IconZoomToFit />
    </button>
</div>

<style>
    button {
        margin: 0;
        padding: 4px;
        height: 40px;
        width: 40px;        
        box-sizing: border-box;

        border: 1px solid #ccc;
        border-radius: 2px;
        color: #000000;

        background-color: #f0f0f0;
    }

    button:hover {
        background-color:#d6d6d6;
    }
</style>